import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import dayjs from 'dayjs';

import utc from 'dayjs/plugin/utc';
import {
  type AssetLevelInputType,
  type FleetLevelInputType,
} from 'views/FleetPlanner/CreateDispatchPlan/Steps/InputStep/types/input-steps.types';
import {
  type DispatchPlanFuelUsageMetricsResponseType,
  type DispatchPlanMaterialDumpResponseType,
  type DispatchPlanTravelTimeMetricsResponseType,
  type DispatchPlanTripsResponseType,
  type PlanDataType,
} from 'views/FleetPlanner/CreateDispatchPlan/Steps/types/steps.types';
import {
  type DispatchPlanSummaryPlanType,
  type DispatchPlanSummaryType,
} from 'views/FleetPlanner/DispatchSummary/types/dispatch-summary.types';

dayjs.extend(utc);

export interface FleetPlanningStateType {
  selectedPlan: 'user' | 'optimized';
  planDispatched: boolean;
  acknowledgedAssets: string[];
  userPlan: {
    planData: PlanDataType[];
    metrics: {
      travel_time: DispatchPlanTravelTimeMetricsResponseType[];
      fuel_usage: DispatchPlanFuelUsageMetricsResponseType[];
      material_dumped: DispatchPlanMaterialDumpResponseType[];
      trips: DispatchPlanTripsResponseType[];
    };
  };
  optimizedPlan: {
    planData: PlanDataType[];
    metrics: {
      travel_time: DispatchPlanTravelTimeMetricsResponseType[];
      fuel_usage: DispatchPlanFuelUsageMetricsResponseType[];
      material_dumped: DispatchPlanMaterialDumpResponseType[];
      trips: DispatchPlanTripsResponseType[];
    };
  };
  isLoading: boolean;
  fleetLevelInput: FleetLevelInputType;
  assetLevelInput: AssetLevelInputType[];
  planTimeWindow: { startTime: string; endTime: string } | null;
  mlInputPayload: Record<string, any>;
  dispatchPlanSummary: DispatchPlanSummaryType;
}

const initialState: FleetPlanningStateType = {
  selectedPlan: 'user',
  planDispatched: false,
  acknowledgedAssets: [],
  userPlan: {
    planData: [],
    metrics: {
      fuel_usage: [],
      travel_time: [],
      material_dumped: [],
      trips: [],
    },
  },
  optimizedPlan: {
    planData: [],
    metrics: {
      fuel_usage: [],
      travel_time: [],
      material_dumped: [],
      trips: [],
    },
  },

  isLoading: false,
  fleetLevelInput: {
    excessIdling: '0',
    lunchTolerance: '0',
  },
  assetLevelInput: [],
  planTimeWindow: null,
  mlInputPayload: {},
  // this is used in dispatch summary plan
  dispatchPlanSummary: {
    planName: '',
    planType: 'no_plan',
    plans: [
      {
        planData: [],
        metrics: {
          fuel_usage: [],
          travel_time: [],
          material_dumped: [],
          trips: [],
        },
        planName: '',
        inputData: {},
      },
    ],
  },
};

export const fleetPlanningSlice = createSlice({
  name: 'fleetPlanning',
  initialState,
  reducers: {
    setUserPlan(
      state,
      action: PayloadAction<{
        planData: PlanDataType[];
        metrics: {
          travel_time: DispatchPlanTravelTimeMetricsResponseType[];
          fuel_usage: DispatchPlanFuelUsageMetricsResponseType[];
          material_dumped: DispatchPlanMaterialDumpResponseType[];
          trips: DispatchPlanTripsResponseType[];
        };
      }>
    ) {
      state.userPlan = action.payload;
    },
    setOptimizedPlan(
      state,
      action: PayloadAction<{
        planData: PlanDataType[];
        metrics: {
          travel_time: DispatchPlanTravelTimeMetricsResponseType[];
          fuel_usage: DispatchPlanFuelUsageMetricsResponseType[];
          material_dumped: DispatchPlanMaterialDumpResponseType[];
          trips: DispatchPlanTripsResponseType[];
        };
      }>
    ) {
      state.optimizedPlan = action.payload;
    },
    setDispatchSummaryPlan(
      state,
      action: PayloadAction<{
        plans: Array<{
          planData: PlanDataType[];
          metrics: {
            travel_time: DispatchPlanTravelTimeMetricsResponseType[];
            fuel_usage: DispatchPlanFuelUsageMetricsResponseType[];
            material_dumped: DispatchPlanMaterialDumpResponseType[];
            trips: DispatchPlanTripsResponseType[];
          };
          inputData: Record<string, any>;
          planName?: string;
        }>;
        planName: string;
        planType: DispatchPlanSummaryPlanType;
      }>
    ) {
      state.dispatchPlanSummary = action.payload;
    },

    setPlans(
      state,
      action: PayloadAction<{
        userPlan: {
          planData: PlanDataType[];
          metrics: {
            travel_time: DispatchPlanTravelTimeMetricsResponseType[];
            fuel_usage: DispatchPlanFuelUsageMetricsResponseType[];
            material_dumped: DispatchPlanMaterialDumpResponseType[];
            trips: DispatchPlanTripsResponseType[];
          };
        };
        optimizedPlan: {
          planData: PlanDataType[];
          metrics: {
            travel_time: DispatchPlanTravelTimeMetricsResponseType[];
            fuel_usage: DispatchPlanFuelUsageMetricsResponseType[];
            material_dumped: DispatchPlanMaterialDumpResponseType[];
            trips: DispatchPlanTripsResponseType[];
          };
        };
        mlInputPayload: Record<string, any>;
      }>
    ) {
      state.userPlan = action.payload.userPlan;
      state.optimizedPlan = action.payload.optimizedPlan;
      state.mlInputPayload = action.payload.mlInputPayload;
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    setFleetLevelInput(state, action: PayloadAction<FleetLevelInputType>) {
      state.fleetLevelInput = action.payload;
    },
    setAssetLevelInput(state, action: PayloadAction<AssetLevelInputType[]>) {
      state.assetLevelInput = action.payload;
    },
    setPlanTimeWindow(
      state,
      action: PayloadAction<{ startTime: string; endTime: string } | null>
    ) {
      state.planTimeWindow = action.payload;
    },

    setSelectedPlan(state, action: PayloadAction<'user' | 'optimized'>) {
      state.selectedPlan = action.payload;
    },

    // to partially update
    updateFleetLevelInput(
      state,
      action: PayloadAction<Partial<FleetLevelInputType>>
    ) {
      state.fleetLevelInput = {
        ...state.fleetLevelInput,
        ...action.payload,
      };
    },

    setPlanDispatched(state, action: PayloadAction<boolean>) {
      state.planDispatched = action.payload;
    },
    addAcknowledgedAsset(state, action: PayloadAction<string>) {
      if (!state.acknowledgedAssets.includes(action.payload)) {
        state.acknowledgedAssets.push(action.payload);
      }
    },
    clearAcknowledgedAssets(state) {
      state.planDispatched = false;
      state.acknowledgedAssets = [];
    },
    resetInputs(state) {
      state.fleetLevelInput = initialState.fleetLevelInput;
      state.assetLevelInput = [];
    },
    resetPlan(
      state,
      action: PayloadAction<
        'userPlan' | 'optimizedPlan' | 'dispatchPlanSummary'
      >
    ) {
      const planType = action.payload;
      if (planType === 'dispatchPlanSummary') {
        state.dispatchPlanSummary = { ...initialState.dispatchPlanSummary };
      } else {
        state[planType] = { ...initialState[planType] };
      }
    },
  },
});

export const {
  setPlans,
  setLoading,
  resetPlan,
  setFleetLevelInput,
  setAssetLevelInput,
  setPlanTimeWindow,
  updateFleetLevelInput,
  setSelectedPlan,
  setDispatchSummaryPlan,
  resetInputs,
  addAcknowledgedAsset,
  clearAcknowledgedAssets,
  setPlanDispatched,
} = fleetPlanningSlice.actions;

export default fleetPlanningSlice.reducer;
